/**
 * Les classes (GDD §3). Module pur.
 *
 * Règle de design : une classe ne donne jamais un pourcentage générique, elle
 * **branche le personnage sur un système différent du jeu**. Acheter un slot,
 * c'est acheter un système, pas un robinet de plus.
 */

export interface EffetClasse {
  /** multiplicateur de vitesse de combat (Xélor) */
  vitesse?: number
  /** multiplicateur de vitesse de récolte (Pandawa) */
  recolte?: number
  /** points de prospection offerts (Enutrof) */
  prospection?: number
  /** multiplicateur de régénération (Eniripsa) */
  regen?: number
  /** multiplicateur de dégâts (Iop, Cra, Sram, Osamodas) */
  degats?: number
  /** points de résistance offerts, en % (Féca) */
  resistance?: number
  /** deux tirages au craft, on garde le meilleur (Écaflip) */
  doubleJet?: boolean
  /** dégâts croissants à mesure que les PV baissent (Sacrieur) */
  rage?: number
}

export interface Classe {
  id: number
  nom: string
  systeme: string
  effet: EffetClasse
}

export const CLASSES: Classe[] = [
  { id: 1, nom: "Féca", systeme: "Résistances élevées, encaisse et monte plus tôt",
    effet: { resistance: 15 } },
  { id: 2, nom: "Osamodas", systeme: "Invocations : une source de dégâts parallèle",
    effet: { degats: 1.25 } },
  { id: 3, nom: "Enutrof", systeme: "Prospection massive, le personnage à butin",
    effet: { prospection: 60 } },
  { id: 4, nom: "Sram", systeme: "Coups critiques élevés",
    effet: { degats: 1.3 } },
  { id: 5, nom: "Xélor", systeme: "Accélère le tick de son personnage",
    effet: { vitesse: 1.4, recolte: 1.4 } },
  { id: 6, nom: "Écaflip", systeme: "Chance au craft : deux jets, garde le meilleur",
    effet: { doubleJet: true } },
  { id: 7, nom: "Eniripsa", systeme: "Régénération élevée, farme au-dessus de son palier",
    effet: { regen: 1.8 } },
  { id: 8, nom: "Iop", systeme: "Dégâts Terre bruts",
    effet: { degats: 1.35 } },
  { id: 9, nom: "Cra", systeme: "Dégâts Air, taux critique",
    effet: { degats: 1.3 } },
  { id: 10, nom: "Sadida", systeme: "Dégâts sur la durée",
    effet: { degats: 1.2, regen: 1.2 } },
  { id: 11, nom: "Sacrieur", systeme: "Plus il subit, plus il tape",
    effet: { rage: 0.6 } },
  { id: 12, nom: "Pandawa", systeme: "Vitesse de récolte, le personnage métier",
    effet: { recolte: 1.8 } },
]

export const classe = (id: number): Classe => CLASSES.find((c) => c.id === id) ?? CLASSES[7]!
export const effetDe = (id: number): EffetClasse => classe(id).effet

/**
 * Coût du slot de personnage suivant (GDD §9).
 *
 * Calibré sur le revenu mesuré en M2 pour un récolteur seul :
 * Revenu mesuré sur le contenu actuel, un joueur qui vend tout : 38 000 k la
 * première heure, 200 000 k en quatre heures, 1,8 M en vingt-quatre. Le deuxième
 * personnage arrive donc dans la première heure et demie, le quatrième après une
 * grosse journée, le dernier reste un objectif de plusieurs milliers d'heures.
 */
export const COUTS_SLOTS = [0, 50_000, 300_000, 1_500_000, 6_000_000, 25_000_000, 100_000_000, 400_000_000]

export const SLOTS_MAX = COUTS_SLOTS.length

export const coutSlot = (nbPersos: number): number | null =>
  nbPersos < SLOTS_MAX ? COUTS_SLOTS[nbPersos]! : null
